/* eslint-disable no-underscore-dangle */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Lazy-import to avoid circular dep: store → slice → callAzureFunctions → store
const getCallAzureFunction = () => import('utils/callAzureFunctions').then((m) => m.default);

export const fetchNotifications = createAsyncThunk(
  'notification/fetchNotifications',
  async ({ filter = 'all', page = 1, pageSize = 20 } = {}) => {
    const callAzureFunction = await getCallAzureFunction();
    const res = await callAzureFunction({
      url: '/user/notifications',
      method: 'get',
      params: { filter, page, page_size: pageSize }
    });
    return { ...res.data, page };
  }
);

export const fetchUnreadCount = createAsyncThunk('notification/fetchUnreadCount', async () => {
  const callAzureFunction = await getCallAzureFunction();
  const res = await callAzureFunction({
    url: '/user/notifications/unread-count',
    method: 'get'
  });
  return res.data.unread_count;
});

export const markAsReadApi = createAsyncThunk('notification/markAsRead', async (ids) => {
  const callAzureFunction = await getCallAzureFunction();
  const idArray = Array.isArray(ids) ? ids : [ids];
  await callAzureFunction({
    url: '/user/notifications/read',
    method: 'put',
    data: { ids: idArray }
  });
  return idArray;
});

export const markAllAsReadApi = createAsyncThunk('notification/markAllAsRead', async () => {
  const callAzureFunction = await getCallAzureFunction();
  const res = await callAzureFunction({
    url: '/user/notifications/read-all',
    method: 'put'
  });
  return res.data.updated;
});

export const deleteNotificationApi = createAsyncThunk('notification/deleteNotification', async (id) => {
  const callAzureFunction = await getCallAzureFunction();
  await callAzureFunction({
    url: '/user/notifications',
    method: 'delete',
    data: { id }
  });
  return id;
});

export const initialState = {
  notifications: [],
  unreadCount: 0,
  total: 0,
  page: 1,
  hasMore: true,
  filter: 'all',
  isLoading: false
};

const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    setFilter: (state, action) => {
      state.filter = action.payload;
      state.page = 1;
      state.hasMore = true;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchNotifications
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const { items, total, page } = action.payload;
        state.notifications = page === 1 ? items : [...state.notifications, ...items];
        state.total = total;
        state.page = page;
        state.hasMore = state.notifications.length < total;
        state.isLoading = false;
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.isLoading = false;
      })

      // fetchUnreadCount
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })

      // markAsRead — optimistic
      .addCase(markAsReadApi.pending, (state, action) => {
        const ids = Array.isArray(action.meta.arg) ? action.meta.arg : [action.meta.arg];
        ids.forEach((id) => {
          const notif = state.notifications.find((n) => n.id === id);
          if (notif && !notif.is_read) {
            notif.is_read = true;
            notif.read_at = new Date().toISOString();
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        });
      })
      .addCase(markAsReadApi.rejected, (state, action) => {
        // Rollback: re-mark as unread
        const ids = Array.isArray(action.meta.arg) ? action.meta.arg : [action.meta.arg];
        ids.forEach((id) => {
          const notif = state.notifications.find((n) => n.id === id);
          if (notif) {
            notif.is_read = false;
            notif.read_at = null;
            state.unreadCount += 1;
          }
        });
      })

      // markAllAsRead — optimistic
      .addCase(markAllAsReadApi.pending, (state) => {
        const now = new Date().toISOString();
        state._prevUnreadCount = state.unreadCount;
        state._prevUnreadIds = state.notifications.filter((n) => !n.is_read).map((n) => n.id);
        state.notifications.forEach((n) => {
          if (!n.is_read) {
            n.is_read = true;
            n.read_at = now;
          }
        });
        state.unreadCount = 0;
      })
      .addCase(markAllAsReadApi.fulfilled, (state) => {
        delete state._prevUnreadCount;
        delete state._prevUnreadIds;
      })
      .addCase(markAllAsReadApi.rejected, (state) => {
        // Rollback
        if (state._prevUnreadIds) {
          state._prevUnreadIds.forEach((id) => {
            const notif = state.notifications.find((n) => n.id === id);
            if (notif) {
              notif.is_read = false;
              notif.read_at = null;
            }
          });
          state.unreadCount = state._prevUnreadCount || 0;
        }
        delete state._prevUnreadCount;
        delete state._prevUnreadIds;
      })

      // deleteNotification — optimistic
      .addCase(deleteNotificationApi.pending, (state, action) => {
        const id = action.meta.arg;
        const notif = state.notifications.find((n) => n.id === id);
        state._deletedNotif = notif ? { ...notif } : null;
        if (notif && !notif.is_read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.notifications = state.notifications.filter((n) => n.id !== id);
        state.total = Math.max(0, state.total - 1);
      })
      .addCase(deleteNotificationApi.fulfilled, (state) => {
        delete state._deletedNotif;
      })
      .addCase(deleteNotificationApi.rejected, (state) => {
        // Rollback
        if (state._deletedNotif) {
          state.notifications.push(state._deletedNotif);
          state.notifications.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          if (!state._deletedNotif.is_read) {
            state.unreadCount += 1;
          }
          state.total += 1;
        }
        delete state._deletedNotif;
      });
  }
});

export const { setFilter } = notificationSlice.actions;

export default notificationSlice.reducer;
